import { prescriptionByNdcApi } from 'api/prescription';
import { map, reduce, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleError } from 'store/thunks/errorHandlerThunks';

const SearchFillHistoryContext = createContext();

export const FillHistorySearchProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [fillHistory, setFillHistory] = useState(undefined);

  const searchFillHistory = useCallback(
    ({ ndc, fillDateFrom, fillDateTo }) => {
      setFillHistory(null);
      return prescriptionByNdcApi({ ndc, fillDateFrom, fillDateTo, limit: 2000, offset: 0 })
        .then(({ count, results: firstPageResult }) => {
          // if there are less than 2000, dont need to make more api calls.
          if (count <= 2000) {
            setFillHistory(
              map(firstPageResult, (v) => {
                const [year, month, day] = v.fillDate.split('-');
                const hasApiOverrides = v.overRides?.length > 0;
                const submittedOverRides = hasApiOverrides
                  ? v.overRides.map(({ ndcCode, quantity }) => ({
                      ndcCode,
                      quantity: String(quantity),
                    }))
                  : undefined;

                return {
                  ...v,
                  fillDate: `${month}/${day}/${year}`,
                  patientName: `${v.patientLastName}, ${v.patientFirstName}`,
                  ndcUpdateSubmitted: hasApiOverrides ? true : undefined,
                  submittedOverRides,
                };
              })
            );
            return Promise.resolve();
          }

          // if there are more than 2000, then make more api calls as needed for other pages
          return Promise.all(
            map(new Array(Math.ceil(count / 2000) - 1), (_, index) =>
              prescriptionByNdcApi({
                ndc,
                fillDateFrom,
                fillDateTo,
                limit: 2000,
                offset: (index + 1) * 2000,
              })
            )
          ).then((combinedResults) => {
            setFillHistory(
              map(
                sortBy(
                  [
                    ...firstPageResult,
                    ...reduce(combinedResults, (all, { results }) => [...all, ...results], []),
                  ],
                  ['fillDate']
                ),
                (v) => {
                  const [year, month, day] = v.fillDate.split('-');
                  const hasApiOverrides = v.overRides?.length > 0;
                  const submittedOverRides = hasApiOverrides
                    ? v.overRides.map(({ ndcCode, quantity }) => ({
                        ndcCode,
                        quantity: String(quantity),
                      }))
                    : undefined;

                  return {
                    ...v,
                    fillDate: `${month}/${day}/${year}`,
                    patientName: `${v.patientLastName}, ${v.patientFirstName}`,
                    ndcUpdateSubmitted: hasApiOverrides ? true : undefined,
                    submittedOverRides,
                  };
                }
              )
            );
          });
        })
        .catch((error) => dispatch(handleError({ error })));
    },
    [dispatch]
  );

  const value = useMemo(
    () => ({ fillHistory, searchFillHistory }),
    [fillHistory, searchFillHistory]
  );

  return (
    <SearchFillHistoryContext.Provider value={value}>{children}</SearchFillHistoryContext.Provider>
  );
};

FillHistorySearchProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useFillHistorySearch = () => useContext(SearchFillHistoryContext);
